<template>
    <div>
        <b-container fluid>
            <b-row v-if="isThereAnyObjectiveToShow">
                <b-col offset="1" cols="10">
                    <b-row no-gutters>
                        <b-col cols="6">
                            <CheckAllContainer v-if="isCheckbox">
                                <b-checkbox
                                    slot="checkbox"
                                    :checked="isAllOKRsCheckedByPage"
                                    @change="onCheckAllOKRs"
                                />
                                <PaginationCountSelection
                                    slot="selection-count"
                                    :count="checkedOKRsCount"
                                    :total="pagination.totalItems"
                                />
                            </CheckAllContainer>
                        </b-col>
                        <b-col cols="6">
                            <ButtonsContainer>
                                <ActionBtn
                                    v-if="isCheckbox"
                                    slot="action-btn"
                                    :id="actionBtnId"
                                    :isUnlink="isUnlink"
                                    :count="checkedOKRsCount"
                                    @click="$emit('onClickActionBtn', $event)"
                                />
                                <PaginationPerPage
                                    slot="per-page"
                                    v-model="pagination.perPage"
                                    @input="$emit('onChangePerPage', $event)"
                                />
                            </ButtonsContainer>
                        </b-col>
                    </b-row>
                    <b-row no-gutters>
                        <b-col>
                            <ObjectiveDetails
                                v-on="$listeners"
                                v-bind="$attrs"
                                v-for="objective in objectives"
                                :key="objective.id"
                                v-model="checkedOKRsIDsObject[objective.id]"
                                @input="onCheckOkrDebounced"
                                :objective="objective"
                                :is-checkbox="isCheckbox"
                                @onClickNewKeyResult="openNewKeyResultModalCallback"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="10" offset="1">
                            <Pagination
                                v-model="pagination.currentPage"
                                :total="pagination.totalItems"
                                :per-page="pagination.perPage"
                                @input="$emit('onChangePage', $event)"
                                class="pagination"
                            />
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row v-else-if="!isLoading && !isThereAnyObjectiveToShow">
                <b-col>
                    <slot name="empty-list" />
                </b-col>
            </b-row>
        </b-container>
        <NewKeyResult
            @onCreatedKeyResult="$listeners.onCreatedKeyResult"
            @openModal="openNewKeyResultModalCallback = $event"
        />
    </div>
</template>

<script>
import { ObjectiveDetails } from '../../objective/details'
import NewKeyResult from '../../key-result/new'
import { ActionBtn, ButtonsContainer, CheckAllContainer } from './components'
import Pagination, { PaginationPerPage, PaginationCountSelection } from '@/views/components/pagination'
import { intersection, debounce } from 'lodash'

export default {
    name: 'OKRs',
    inheritAttrs: false,
    props: {
        objectives: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
        isCheckbox: {
            type: Boolean,
            required: false,
        },
        isUnlink: {
            type: Boolean,
            required: false,
        },
        pagination: {
            type: Object,
            required: true,
            default: () => ({
                perPage: 10,
                totalItems: 0,
                currentPage: 1,
            }),
        },
    },
    components: {
        NewKeyResult,
        ObjectiveDetails,
        // components
        ActionBtn,
        ButtonsContainer,
        CheckAllContainer,
        // pagination
        Pagination,
        PaginationPerPage,
        PaginationCountSelection,
    },
    data() {
        return {
            checkedOKRsByPage: {},
            checkedOKRsIDsArray: [],
            checkedOKRsIDsObject: {},
            openNewKeyResultModalCallback: () => {},
        }
    },
    created() {
        this.updatePagination()
        this.emitResetCheckedOKRs()
    },
    computed: {
        isThereAnyObjectiveToShow() {
            return this.objectives && this.objectives.length > 0
        },
        checkedOKRsCount() {
            return Object.keys(this.checkedOKRsIDsObject).reduce((count, okrID) => {
                return this.checkedOKRsIDsObject[okrID] ? ++count : count
            }, 0)
        },
        isAllOKRsCheckedByPage() {
            if (!this.checkedOKRsByPage[this.pagination.currentPage]) {
                return false
            }
            return this.checkedOKRsByPage[this.pagination.currentPage].isAllChecked
        },
        actionBtnId() {
            return this.isUnlink ? 'btn-strategy-unlink-objectives' : 'btn-strategy-link-objectives'
        },
    },
    methods: {
        updatePagination() {
            this.$emit('update:pagination', this.pagination)
        },
        emitResetCheckedOKRs() {
            this.$emit('resetCheckedOKRs', this.resetCheckedOKRs)
        },
        resetCheckedOKRs() {
            this.checkedOKRsIDsArray = []
            this.checkedOKRsIDsObject = {}
            this.updateCheckedOKRsIDs()
        },
        onCheckAllOKRs(isChecked) {
            const previousPages = this.checkedOKRsIDsObject
            this.checkedOKRsIDsObject = Object.keys(this.objectives).reduce((checkedOKRsIDsObject, index) => {
                return {
                    ...previousPages,
                    ...checkedOKRsIDsObject,
                    [this.objectives[index].id]: isChecked,
                }
            }, {})
        },
        onCheckOkrDebounced: debounce(function () {
            this.setCheckedOKRsIDsArray()
            this.updateCheckedOKRsIDs()
            this.setCheckedOKRsByPage()
        }, 150),
        setCheckedOKRsIDsArray() {
            this.checkedOKRsIDsArray = Object.keys(this.checkedOKRsIDsObject).filter((checkedOkrId) => {
                return this.checkedOKRsIDsObject[checkedOkrId]
            })
        },
        updateCheckedOKRsIDs() {
            this.$emit('checkedOKRsIDs', this.checkedOKRsIDsArray)
        },
        setCheckedOKRsByPage() {
            const OKRsIDs = this.objectives.map((o) => o.id)
            const checkedOKRsIDs = this.checkedOKRsIDsArray.map((c) => parseInt(c))
            const intersectionLength = intersection(OKRsIDs, checkedOKRsIDs).length
            this.checkedOKRsByPage = {
                ...this.checkedOKRsByPage,
                [this.pagination.currentPage]: {
                    OKRsIDs: checkedOKRsIDs,
                    isAllChecked: intersectionLength === this.objectives.length,
                },
            }
        },
    },
}
</script>
